import React, { useState } from 'react';
import { US_STATES } from '../../lib/utils';
import { NANO_ORDER_EMAIL } from '../../lib/emails';
import { useRescueContext } from '../../lib/RescueContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalField } from '../../components/FormField/PortalField';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { FormGrid } from '../../components/FormField/FormGrid';
import { trackChipsOrderSubmitted } from '../../analytics';
import styles from './Chips.module.scss';

interface OrderChipsModalProps {
  open: boolean;
  onClose: () => void;
}

const QTY_OPTIONS = [
  { value: '20', label: '20' },
  { value: '40', label: '40' },
  { value: '60', label: '60' },
  { value: '80', label: '80' },
  { value: '100', label: '100' },
  { value: 'other', label: 'Other' },
];

export default function OrderChipsModal({ open, onClose }: OrderChipsModalProps) {
  const { orgName } = useRescueContext();
  const [quantity, setQuantity] = useState('');
  const [customQty, setCustomQty] = useState('');
  const [street, setStreet] = useState('');
  const [apt, setApt] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  function reset() {
    setQuantity('');
    setCustomQty('');
    setStreet('');
    setApt('');
    setCity('');
    setState('');
    setZip('');
    setErrors({});
  }

  function handleClose() {
    reset();
    onClose();
  }

  function validate(): boolean {
    const errs: Record<string, boolean> = {};
    const finalQty = quantity === 'other' ? parseInt(customQty) : parseInt(quantity);
    if (!finalQty || finalQty <= 0) errs.quantity = true;
    if (!street.trim()) errs.street = true;
    if (!city.trim()) errs.city = true;
    if (!state) errs.state = true;
    if (!zip.trim() || !/^\d{5}$/.test(zip)) errs.zip = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;

    const finalQty = quantity === 'other' ? parseInt(customQty) : parseInt(quantity);
    const boxCount = Math.ceil(finalQty / 20);
    const address = [street, apt ? `Apt ${apt}` : '', `${city}, ${state} ${zip}`].filter(Boolean).join(', ');

    const subject = encodeURIComponent(`Microchip Order - ${orgName}`);
    const body = encodeURIComponent(
      `Organization: ${orgName}\nQuantity: ${finalQty} chips (${boxCount} box${
        boxCount > 1 ? 'es' : ''
      })\n\nShipping Address:\n${address}`,
    );

    trackChipsOrderSubmitted(finalQty);
    window.open(`mailto:${NANO_ORDER_EMAIL}?subject=${subject}&body=${body}`, '_self');
    handleClose();
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Nano Microchips</ModalTitle>
      <p className={styles.modalDescription}>
        Order free Nano microchip boxes for <strong>{orgName}</strong>.
      </p>

      <PortalField
        label="Quantity *"
        hint="Chips come in boxes of 20"
        error="Select a quantity"
        showError={errors.quantity}
        fullWidth
        style={{ marginBottom: 24 }}
      >
        <ToggleGroup options={QTY_OPTIONS} value={quantity} onChange={setQuantity} />
        {quantity === 'other' && (
          <PortalInput
            value={customQty}
            onChange={(e) => setCustomQty(e.target.value)}
            placeholder="Enter quantity"
            type="number"
            style={{ marginTop: 8, width: 160 }}
            hasError={errors.quantity}
          />
        )}
      </PortalField>

      <p className={styles.sectionTitle}>Shipping Address</p>
      <FormGrid>
        <PortalField label="Street Address *" error="Required" showError={errors.street} fullWidth>
          <PortalInput
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="Street address"
            hasError={errors.street}
          />
        </PortalField>

        <PortalField label="Apt / Suite">
          <PortalInput value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt / Suite" />
        </PortalField>

        <PortalField label="City *" error="Required" showError={errors.city}>
          <PortalInput
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            hasError={errors.city}
          />
        </PortalField>

        <PortalField label="State *" error="Required" showError={errors.state}>
          <PortalSelect value={state} onChange={(e) => setState(e.target.value)} hasError={errors.state}>
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </PortalSelect>
        </PortalField>

        <PortalField label="Zip Code *" error="5-digit zip required" showError={errors.zip}>
          <PortalInput
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="Zip code"
            hasError={errors.zip}
          />
        </PortalField>
      </FormGrid>

      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit}>
          Submit Request
        </Button>
      </ModalActions>
    </Modal>
  );
}
